import { useCallback, useEffect, useState, useRef } from 'react';
import { PanelState } from './PanelTypes';
import { TransitionStep, TransitionSequence } from './shared-types';

// This type defines what we need from PanelContext without importing it directly
interface PanelContextInterface {
  state: {
    panels: Record<string, {
      relationships?: Array<{
        with: string;
        behavior: string;
        timing?: 'before' | 'after' | 'simultaneous';
        delay?: number;
      }>;
    }>;
  };
  setPanelState: (id: string, state: PanelState, options?: { skipRelationships?: boolean }) => void;
}

// Hook that takes the panel context as a parameter rather than importing it
export function useTransitionOrchestrator(panelContext: PanelContextInterface) {
  const { state, setPanelState } = panelContext;
  const [sequences, setSequences] = useState<TransitionSequence[]>([]);
  const [activeSequence, setActiveSequence] = useState<string | null>(null);
  const completedSteps = useRef<Set<string>>(new Set());
  const pendingTimeouts = useRef<number[]>([]);
  
  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      pendingTimeouts.current.forEach(timeoutId => clearTimeout(timeoutId));
    };
  }, []);
  
  // Process the next available transition step
  const processNextStep = useCallback(() => {
    if (!activeSequence) return;
    
    const sequence = sequences.find(seq => seq.id === activeSequence);
    if (!sequence) return;
    
    // Find the next executable steps (multiple can be executed simultaneously)
    const executableSteps = sequence.steps.filter(step => {
      // Check if dependencies are satisfied
      if (step.dependencies?.length) {
        return step.dependencies.every(depId => 
          completedSteps.current.has(`${activeSequence}-${depId}`)
        );
      }
      // No dependencies, step is executable
      return true;
    });
    
    if (executableSteps.length > 0) {
      // Execute all eligible steps
      executableSteps.forEach(step => {
        const { panelId, action, delay } = step;
        
        const timeoutId = window.setTimeout(() => {
          let newState: PanelState;
          switch (action) {
            case 'show': newState = 'visible'; break;
            case 'hide': newState = 'hidden'; break;
            case 'collapse': newState = 'collapsed'; break;
            default: newState = 'visible';
          }
          
          setPanelState(panelId, newState, { skipRelationships: true });
          
          // Mark step as completed
          completedSteps.current.add(`${activeSequence}-${panelId}`);
          
          // Remove the executed steps
          setSequences(prev => 
            prev.map(seq => 
              seq.id === activeSequence
                ? { 
                    ...seq, 
                    steps: seq.steps.filter(s => !executableSteps.includes(s)) 
                  }
                : seq
            )
          );
          
          // Remove from pending timeouts
          pendingTimeouts.current = pendingTimeouts.current.filter(id => id !== timeoutId);
          
          // Process next steps after this one completes
          processNextStep();
        }, delay);
        
        // Track the timeout for cleanup
        pendingTimeouts.current.push(timeoutId);
      });
    } else if (sequence.steps.length === 0) {
      // Sequence is complete
      if (sequence.onComplete) {
        sequence.onComplete();
      }
      
      // Remove the sequence
      setSequences(prev => prev.filter(seq => seq.id !== activeSequence));
      setActiveSequence(null);
      completedSteps.current.clear();
      
      // Check if there's another sequence waiting
      if (sequences.length > 1) {
        const nextSequence = sequences.find(seq => seq.id !== activeSequence);
        if (nextSequence) {
          setActiveSequence(nextSequence.id);
        }
      }
    }
  }, [activeSequence, sequences, setPanelState]);
  
  // Start or continue processing when things change
  useEffect(() => {
    if (activeSequence) {
      processNextStep();
    } else if (sequences.length > 0) {
      // Start a new sequence
      setActiveSequence(sequences[0].id);
    }
  }, [activeSequence, sequences, processNextStep]);
  
  // Start a new transition sequence
  const startSequence = useCallback((sequence: TransitionSequence) => {
    setSequences(prev => [...prev, sequence]);
    
    // If no active sequence, start this one immediately
    if (!activeSequence) {
      setActiveSequence(sequence.id);
    }
  }, [activeSequence]);
  
  // Create a sequence from panel relationships
  const createSequenceFromRelationships = useCallback((
    targetId: string, 
    targetState: PanelState
  ) => {
    const targetPanel = state.panels[targetId];
    if (!targetPanel || !targetPanel.relationships?.length) return null;
    
    const steps: TransitionStep[] = [];
    const sequenceId = `seq-${Date.now()}-${targetId}-${targetState}`;
    
    // Add steps based on relationships
    targetPanel.relationships.forEach(rel => {
      const { with: relatedId, behavior, timing = 'simultaneous', delay = 0 } = rel;
      
      // Skip if the related panel doesn't exist
      if (!state.panels[relatedId]) return;
      
      switch (behavior) {
        case 'collapse-when-opens':
          if (targetState === 'visible') {
            if (timing === 'before') {
              // Related panel collapses first
              steps.push({
                panelId: relatedId,
                action: 'collapse',
                delay: 0
              });
              
              // Then target panel opens
              steps.push({
                panelId: targetId,
                action: 'show',
                delay,
                dependencies: [relatedId]
              });
            } else if (timing === 'after') {
              // Target panel opens first
              steps.push({
                panelId: targetId,
                action: 'show',
                delay: 0
              });
              
              // Then related panel collapses
              steps.push({
                panelId: relatedId,
                action: 'collapse',
                delay,
                dependencies: [targetId]
              });
            } else {
              // Simultaneous
              steps.push({
                panelId: targetId,
                action: 'show',
                delay: 0
              });
              steps.push({
                panelId: relatedId,
                action: 'collapse',
                delay: 0
              });
            }
          }
          break;
        
        case 'open-when-collapses':
          if (targetState === 'collapsed') {
            if (timing === 'before') {
              // Related panel opens first
              steps.push({
                panelId: relatedId,
                action: 'show',
                delay: 0
              });
              
              // Then target panel collapses
              steps.push({
                panelId: targetId,
                action: 'collapse',
                delay,
                dependencies: [relatedId]
              });
            } else if (timing === 'after') {
              // Target panel collapses first
              steps.push({
                panelId: targetId,
                action: 'collapse',
                delay: 0
              });
              
              // Then related panel opens
              steps.push({
                panelId: relatedId,
                action: 'show',
                delay,
                dependencies: [targetId]
              });
            } else {
              // Simultaneous
              steps.push({
                panelId: targetId,
                action: 'collapse',
                delay: 0
              });
              steps.push({
                panelId: relatedId,
                action: 'show',
                delay: 0
              });
            }
          }
          break;
          
        case 'hide-when-shows':
          if (targetState === 'visible') {
            if (timing === 'before') {
              // Related panel hides first
              steps.push({
                panelId: relatedId,
                action: 'hide',
                delay: 0
              });
              
              // Then target panel shows
              steps.push({
                panelId: targetId,
                action: 'show',
                delay,
                dependencies: [relatedId]
              });
            } else if (timing === 'after') {
              // Target panel shows first
              steps.push({
                panelId: targetId,
                action: 'show',
                delay: 0
              });
              
              // Then related panel hides
              steps.push({
                panelId: relatedId,
                action: 'hide',
                delay,
                dependencies: [targetId]
              });
            } else {
              // Simultaneous
              steps.push({
                panelId: targetId,
                action: 'show',
                delay: 0
              });
              steps.push({
                panelId: relatedId,
                action: 'hide',
                delay: 0
              });
            }
          }
          break;
          
        case 'show-when-hides':
          if (targetState === 'hidden') {
            if (timing === 'before') {
              // Related panel shows first
              steps.push({
                panelId: relatedId,
                action: 'show',
                delay: 0
              });
              
              // Then target panel hides
              steps.push({
                panelId: targetId,
                action: 'hide',
                delay,
                dependencies: [relatedId]
              });
            } else if (timing === 'after') {
              // Target panel hides first
              steps.push({
                panelId: targetId,
                action: 'hide',
                delay: 0
              });
              
              // Then related panel shows
              steps.push({
                panelId: relatedId,
                action: 'show',
                delay,
                dependencies: [targetId]
              });
            } else {
              // Simultaneous
              steps.push({
                panelId: targetId,
                action: 'hide',
                delay: 0
              });
              steps.push({
                panelId: relatedId,
                action: 'show',
                delay: 0
              });
            }
          }
          break;
      }
    });
    
    if (steps.length === 0) {
      // If no relationship steps were created but we have a target state change,
      // create a single step for the target panel
      steps.push({
        panelId: targetId,
        action: targetState === 'visible' ? 'show' : 
                targetState === 'hidden' ? 'hide' : 'collapse',
        delay: 0
      });
    }
    
    return {
      id: sequenceId,
      steps
    };
  }, [state.panels]);
  
  // Execute a transition with relationships
  const executeTransition = useCallback((targetId: string, targetState: PanelState) => {
    const sequence = createSequenceFromRelationships(targetId, targetState);
    
    if (sequence) {
      startSequence(sequence);
      return true;
    } else {
      // No sequence, just set the state directly
      setPanelState(targetId, targetState, { skipRelationships: true });
      return false;
    }
  }, [createSequenceFromRelationships, startSequence, setPanelState]);
  
  // Cancel all running transitions
  const cancelAllTransitions = useCallback(() => {
    pendingTimeouts.current.forEach(timeoutId => clearTimeout(timeoutId));
    pendingTimeouts.current = [];
    completedSteps.current.clear();
    setSequences([]);
    setActiveSequence(null);
  }, []);
  
  return {
    startSequence,
    executeTransition,
    cancelAllTransitions,
    isProcessing: activeSequence !== null,
    activeSequenceId: activeSequence
  };
}